import React, { useRef, useState } from 'react';
import { Button, Card, Col, Empty, List, Modal, Row, Spin, Tag, Typography, Divider, Alert } from 'antd';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import { renderMoney } from '@/utils/helpers';
import { isMobile } from 'react-device-detect';
import { getCustomerServiceFinancialHistory } from '@/redux/actions/customerServicesActions';

const { Text } = Typography;

const adjustmentStatusText = status => ({
    PENDING: 'Chờ xử lý',
    APPROVED: 'Đã ghi nhận',
    REJECTED: 'Đã từ chối',
    COMPLETED: 'Đã hoàn tất',
}[status] || 'Đã ghi nhận');

const adjustmentOriginText = origin => ({
    CUSTOMER_SERVICE_FINANCIAL_CHANGE: 'Thay đổi giá trị dịch vụ',
    CUSTOMER_SERVICE_SELECTED_AT_CHANGE: 'Thay đổi ngày chốt dịch vụ',
}[origin] || 'Điều chỉnh tự động từ dịch vụ');

const commissionImpactText = ids => {
    const count = (ids || []).length;
    return count ? `${count} khoản hoa hồng được cập nhật` : 'Không phát sinh thay đổi hoa hồng';
};

export default function CustomerServiceFinancialHistory({ customerService }) {
    const dispatch = useDispatch();
    const [visible, setVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [items, setItems] = useState([]);
    const [selectedAtItems, setSelectedAtItems] = useState([]);
    const [commissionDeltaItems, setCommissionDeltaItems] = useState([]);
    const loadRequestRef = useRef(0);

    const open = async () => {
        const serviceId = Number(customerService?.id) || null;
        if (!serviceId) return;
        const requestId = loadRequestRef.current + 1;
        loadRequestRef.current = requestId;
        setVisible(true);
        setLoading(true);
        setItems([]);
        setSelectedAtItems([]);
        setCommissionDeltaItems([]);

        try {
            const history = await dispatch(getCustomerServiceFinancialHistory(serviceId));
            if (loadRequestRef.current !== requestId) return;
            setItems(history.financialAdjustments);
            setSelectedAtItems(history.selectedAtAdjustments);
            setCommissionDeltaItems(history.commissionAdjustments);
        } catch (error) {
            // Redux Action đã hiển thị lỗi theo nội dung backend.
        } finally {
            if (loadRequestRef.current === requestId) setLoading(false);
        }
    };

    const close = () => {
        loadRequestRef.current += 1;
        setVisible(false);
        setLoading(false);
        setItems([]);
        setSelectedAtItems([]);
        setCommissionDeltaItems([]);
    };

    const styles = {
        section: {
            marginBottom: 16,
        },

        sectionHeader: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            marginBottom: 8,
        },

        sectionTitle: {
            margin: 0,
            fontSize: 18,
            lineHeight: '24px',
            fontWeight: 700,
            color: 'rgba(0, 0, 0, 0.88)',
        },

        sectionCount: {
            fontSize: 14,
            lineHeight: '20px',
            whiteSpace: 'nowrap',
        },

        listItem: {
            padding: '0 0 8px',
            border: 0,
        },

        card: {
            width: '100%',
            borderRadius: 8,
            overflow: 'hidden',
        },

        cardBody: {
            padding: isMobile ? 10 : 12,
        },

        header: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 6,
            paddingBottom: 8,
            marginBottom: 8,
            borderBottom: '1px solid #f0f0f0',
        },

        headerLeft: {
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 6,
        },

        headerRight: {
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 6,
        },

        time: {
            fontSize: 15,
            lineHeight: '22px',
            fontWeight: 600,
            color: 'rgba(0, 0, 0, 0.88)',
        },

        actor: {
            fontSize: 15,
            lineHeight: '22px',
            fontWeight: 600,
            color: 'rgba(0, 0, 0, 0.88)',
        },

        metaLabel: {
            fontSize: 13,
            lineHeight: '20px',
            color: 'rgba(0, 0, 0, 0.45)',
        },

        group: {
            marginTop: 0,
        },

        groupTitle: {
            marginBottom: 4,
            fontSize: 14,
            lineHeight: '20px',
            fontWeight: 700,
            color: 'rgba(0, 0, 0, 0.65)',
        },

        metric: {
            minHeight: 46,
            padding: '4px 8px 4px 0',
        },

        metricLabel: {
            display: 'block',
            marginBottom: 2,
            fontSize: 13,
            lineHeight: '20px',
            color: 'rgba(0, 0, 0, 0.45)',
        },

        metricValue: {
            fontSize: 16,
            lineHeight: '22px',
            fontWeight: 600,
            color: 'rgba(0, 0, 0, 0.88)',
            wordBreak: 'break-word',
        },

        oldValue: {
            color: 'rgba(0, 0, 0, 0.45)',
            fontWeight: 500,
        },

        newValue: {
            color: 'rgba(0, 0, 0, 0.88)',
            fontWeight: 700,
        },

        arrow: {
            display: 'inline-block',
            margin: '0 6px',
            color: 'rgba(0, 0, 0, 0.30)',
            fontWeight: 400,
        },

        divider: {
            margin: '6px 0 8px',
        },

        footer: {
            marginTop: 6,
            paddingTop: 6,
            borderTop: '1px solid #f0f0f0',
        },

        footerRow: {
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
            padding: '2px 0',
        },

        footerLabel: {
            width: isMobile ? 110 : 140,
            flexShrink: 0,
            fontSize: 13,
            lineHeight: '20px',
            color: 'rgba(0, 0, 0, 0.45)',
        },

        footerValue: {
            flex: 1,
            minWidth: 0,
            fontSize: 14,
            lineHeight: '20px',
            fontWeight: 500,
            color: 'rgba(0, 0, 0, 0.75)',
            wordBreak: 'break-word',
        },

        deltaTag: {
            margin: 0,
            padding: '2px 8px',
            fontSize: 16,
            lineHeight: '22px',
            fontWeight: 700,
        },
    };

    const SectionHeader = ({ title, count }) => (
        <div style={styles.sectionHeader}>
            <div style={styles.sectionTitle}>{title}</div>
            <Text type="secondary" style={styles.sectionCount}>
                {count} thay đổi
            </Text>
        </div>
    );

    const Metric = ({ label, children }) => (
        <div style={styles.metric}>
            <span style={styles.metricLabel}>{label}</span>
            <div style={styles.metricValue}>{children}</div>
        </div>
    );

    const ChangeValue = ({ before, after }) => (
        <>
            <span style={styles.oldValue}>{before}</span>
            <span style={styles.arrow}>→</span>
            <span style={styles.newValue}>{after}</span>
        </>
    );

    const HistoryHeader = ({ time, actor, extra }) => (
        <div style={styles.header}>
            <div style={styles.headerLeft}>
                <span style={styles.metaLabel}>Thời gian</span>
                <span style={styles.time}>{time}</span>
            </div>

            <div style={styles.headerRight}>
                {extra}
                <span style={styles.metaLabel}>Người thực hiện</span>
                <span style={styles.actor}>{actor}</span>
            </div>
        </div>
    );

    const FooterRow = ({ label, children }) => (
        <div style={styles.footerRow}>
            <div style={styles.footerLabel}>{label}</div>
            <div style={styles.footerValue}>{children}</div>
        </div>
    );

    return (
        <>
            <Button onClick={open}>Lịch sử thay đổi</Button>

            <Modal
                visible={visible}
                title="Lịch sử thay đổi dịch vụ"
                footer={null}
                width={isMobile ? '100%' : '90%'}
                onCancel={close}
                maskClosable={false}
                bodyStyle={{
                    padding: isMobile ? 10 : 14,
                    background: '#f7f8fa',
                }}
            >
                <Spin spinning={loading}>
                    <Alert
                        type="info"
                        message="Lịch sử này giúp đối chiếu các thay đổi đã ảnh hưởng đến giá trị dịch vụ, công nợ và hoa hồng. Các giao dịch gốc luôn được giữ lại."
                        style={{ marginBottom: 12 }}
                    />
                    {!items.length &&
                        !selectedAtItems.length &&
                        !commissionDeltaItems.length &&
                        !loading ? (
                        <Empty description="Chưa có thay đổi" />
                    ) : (
                        <>
                            {/* ĐIỀU CHỈNH HOA HỒNG */}
                            {!!commissionDeltaItems.length && (
                                <div style={styles.section}>
                                    <SectionHeader
                                        title="Điều chỉnh hoa hồng tự động"
                                        count={commissionDeltaItems.length}
                                    />

                                    <List
                                        split={false}
                                        dataSource={commissionDeltaItems}
                                        renderItem={item => (
                                            <List.Item style={styles.listItem}>
                                                <Card
                                                    size="small"
                                                    bordered
                                                    style={styles.card}
                                                    bodyStyle={styles.cardBody}
                                                >
                                                    <HistoryHeader
                                                        time={moment(item.created_at).format(
                                                            'DD/MM/YYYY HH:mm:ss'
                                                        )}
                                                        actor={
                                                            item.actor_name ||
                                                            (Number(item.initiated_by) === 0
                                                                ? 'Hệ thống'
                                                                : 'Không xác định')
                                                        }
                                                        extra={
                                                            <Tag color="green" style={{ margin: 0 }}>
                                                                {adjustmentStatusText(item.status)}
                                                            </Tag>
                                                        }
                                                    />

                                                    <div style={styles.group}>
                                                        <Row gutter={[16, 0]}>
                                                            <Col xs={24} md={6}>
                                                                <Metric label="Người khởi tạo">
                                                                    {item.actor_name ||
                                                                        (Number(item.initiated_by) === 0
                                                                            ? 'Hệ thống'
                                                                            : 'Không xác định')}
                                                                </Metric>
                                                            </Col>

                                                            <Col xs={24} md={6}>
                                                                <Metric label="Nguyên nhân phát sinh">
                                                                    {adjustmentOriginText(item.origin_type)}
                                                                </Metric>
                                                            </Col>

                                                            <Col xs={24} md={6}>
                                                                <Metric label="Trạng thái">
                                                                    <Tag
                                                                        color="green"
                                                                        style={{
                                                                            margin: 0,
                                                                            fontSize: 14,
                                                                        }}
                                                                    >
                                                                        {adjustmentStatusText(item.status)}
                                                                    </Tag>
                                                                </Metric>
                                                            </Col>

                                                            <Col xs={24} md={6}>
                                                                <Metric label="Doanh thu ghi nhận">
                                                                    <ChangeValue
                                                                        before={renderMoney(
                                                                            item.before_recognized
                                                                        )}
                                                                        after={renderMoney(
                                                                            item.after_recognized
                                                                        )}
                                                                    />
                                                                </Metric>
                                                            </Col>

                                                            <Col xs={24}>
                                                                <Metric label="Ảnh hưởng đến hoa hồng">
                                                                    {commissionImpactText(item.impact_snapshot?.commission_adjustment_ids)}
                                                                </Metric>
                                                            </Col>
                                                        </Row>
                                                    </div>

                                                    <div style={styles.footer}>
                                                        <FooterRow label="Lý do">
                                                            {item.reason}
                                                        </FooterRow>

                                                    </div>
                                                </Card>
                                            </List.Item>
                                        )}
                                    />
                                </div>
                            )}

                            {/* THAY ĐỔI NGÀY CHỐT */}
                            {!!selectedAtItems.length && (
                                <div style={styles.section}>
                                    <SectionHeader
                                        title="Thay đổi ngày chốt"
                                        count={selectedAtItems.length}
                                    />

                                    <List
                                        split={false}
                                        dataSource={selectedAtItems}
                                        renderItem={item => {
                                            const impact = item.impact_snapshot || {};

                                            return (
                                                <List.Item style={styles.listItem}>
                                                    <Card
                                                        size="small"
                                                        bordered
                                                        style={styles.card}
                                                        bodyStyle={styles.cardBody}
                                                    >
                                                        <HistoryHeader
                                                            time={moment(item.created_at).format(
                                                                'DD/MM/YYYY HH:mm:ss'
                                                            )}
                                                            actor={
                                                                item.actor_name ||
                                                                'Không xác định'
                                                            }
                                                        />

                                                        <div style={styles.group}>
                                                            <Row gutter={[16, 0]}>
                                                                <Col xs={12} md={6}>
                                                                    <Metric label="Ngày chốt cũ">
                                                                        {item.old_selected_at
                                                                            ? moment(
                                                                                item.old_selected_at
                                                                            ).format(
                                                                                'DD/MM/YYYY'
                                                                            )
                                                                            : '-'}
                                                                    </Metric>
                                                                </Col>

                                                                <Col xs={12} md={6}>
                                                                    <Metric label="Ngày chốt mới">
                                                                        {moment(
                                                                            item.new_selected_at
                                                                        ).format(
                                                                            'DD/MM/YYYY'
                                                                        )}
                                                                    </Metric>
                                                                </Col>

                                                                <Col xs={24} md={6}>
                                                                    <Metric label="Kỳ thống kê">
                                                                        <ChangeValue
                                                                            before={
                                                                                item.old_period_key
                                                                            }
                                                                            after={
                                                                                item.new_period_key
                                                                            }
                                                                        />
                                                                    </Metric>
                                                                </Col>

                                                                <Col xs={24} md={6}>
                                                                    <Metric label="Ảnh hưởng đến hoa hồng">
                                                                        {commissionImpactText(impact.commission_adjustment_ids)}
                                                                    </Metric>
                                                                </Col>
                                                            </Row>
                                                        </div>

                                                        <div style={styles.footer}>
                                                            <FooterRow label="Lý do">
                                                                {item.reason}
                                                            </FooterRow>

                                                        </div>
                                                    </Card>
                                                </List.Item>
                                            );
                                        }}
                                    />
                                </div>
                            )}

                            {/* THAY ĐỔI THÔNG TIN DỊCH VỤ */}
                            {!!items.length && (
                                <div style={styles.section}>
                                    <SectionHeader
                                        title="Thay đổi thông tin dịch vụ"
                                        count={items.length}
                                    />

                                    <List
                                        split={false}
                                        dataSource={items}
                                        renderItem={item => {
                                            const impact = item.impact_snapshot || {};
                                            const deltaAmount = Number(item.delta_amount || 0);

                                            return (
                                                <List.Item style={styles.listItem}>
                                                    <Card
                                                        size="small"
                                                        bordered
                                                        style={styles.card}
                                                        bodyStyle={styles.cardBody}
                                                    >
                                                        <HistoryHeader
                                                            time={moment(item.effective_at).format(
                                                                'DD/MM/YYYY HH:mm:ss'
                                                            )}
                                                            actor={
                                                                item.actor_name ||
                                                                'Không xác định'
                                                            }
                                                        />

                                                        <div style={styles.group}>
                                                            <div style={styles.groupTitle}>
                                                                Giá trị dịch vụ
                                                            </div>

                                                            <Row gutter={[16, 0]}>
                                                                <Col xs={12} md={6}>
                                                                    <Metric label="Số lượng">
                                                                        <ChangeValue
                                                                            before={
                                                                                item.old_quantity
                                                                            }
                                                                            after={
                                                                                item.new_quantity
                                                                            }
                                                                        />
                                                                    </Metric>
                                                                </Col>

                                                                <Col xs={24} md={6}>
                                                                    <Metric label="Giá bán">
                                                                        <ChangeValue
                                                                            before={renderMoney(
                                                                                item.old_sale_price
                                                                            )}
                                                                            after={renderMoney(
                                                                                item.new_sale_price
                                                                            )}
                                                                        />
                                                                    </Metric>
                                                                </Col>

                                                                <Col xs={24} md={6}>
                                                                    <Metric label="Thành tiền">
                                                                        <ChangeValue
                                                                            before={renderMoney(
                                                                                item.old_amount
                                                                            )}
                                                                            after={renderMoney(
                                                                                item.new_amount
                                                                            )}
                                                                        />
                                                                    </Metric>
                                                                </Col>

                                                                <Col xs={24} md={6}>
                                                                    <Metric label="Chênh lệch">
                                                                        <Tag
                                                                            color={
                                                                                deltaAmount < 0
                                                                                    ? 'red'
                                                                                    : 'green'
                                                                            }
                                                                            style={
                                                                                styles.deltaTag
                                                                            }
                                                                        >
                                                                            {renderMoney(
                                                                                item.delta_amount
                                                                            )}
                                                                        </Tag>
                                                                    </Metric>
                                                                </Col>
                                                            </Row>
                                                        </div>

                                                        <Divider style={styles.divider} />

                                                        <div style={styles.group}>
                                                            <div style={styles.groupTitle}>
                                                                Công nợ và số dư
                                                            </div>

                                                            <Row gutter={[16, 0]}>
                                                                <Col xs={24} md={6}>
                                                                    <Metric label="Công nợ">
                                                                        <ChangeValue
                                                                            before={renderMoney(
                                                                                item.before_debt
                                                                            )}
                                                                            after={renderMoney(
                                                                                item.after_debt
                                                                            )}
                                                                        />
                                                                    </Metric>
                                                                </Col>

                                                                <Col xs={24} md={6}>
                                                                    <Metric label="Số dư">
                                                                        <ChangeValue
                                                                            before={renderMoney(
                                                                                item.before_balance
                                                                            )}
                                                                            after={renderMoney(
                                                                                item.after_balance
                                                                            )}
                                                                        />
                                                                    </Metric>
                                                                </Col>

                                                                <Col xs={24} md={6}>
                                                                    <Metric label="Hoàn về số dư">
                                                                        {renderMoney(
                                                                            impact.released_to_balance ||
                                                                            0
                                                                        )}
                                                                    </Metric>
                                                                </Col>

                                                                <Col xs={24} md={6}>
                                                                    <Metric label="Tự động dùng số dư">
                                                                        {renderMoney(
                                                                            impact.auto_applied_balance ||
                                                                            0
                                                                        )}
                                                                    </Metric>
                                                                </Col>

                                                                <Col xs={24} md={6}>
                                                                    <Metric label="Công nợ tăng thêm">
                                                                        {renderMoney(
                                                                            impact.uncovered_increase ||
                                                                            0
                                                                        )}
                                                                    </Metric>
                                                                </Col>

                                                                <Col xs={24} md={18}>
                                                                    <Metric label="Ảnh hưởng đến hoa hồng">
                                                                        {commissionImpactText(impact.commission_adjustment_ids)}
                                                                    </Metric>
                                                                </Col>
                                                            </Row>
                                                        </div>

                                                        <div style={styles.footer}>
                                                            <FooterRow label="Lý do">
                                                                {item.reason}
                                                            </FooterRow>

                                                        </div>
                                                    </Card>
                                                </List.Item>
                                            );
                                        }}
                                    />
                                </div>
                            )}
                        </>
                    )}
                </Spin>
            </Modal>
        </>
    );
}
